import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import {
  CATEGORIES,
  CATEGORY_META,
  PRODUCTS,
  PRODUCT_THEME,
  STATUSES,
  STATUS_META,
} from '../lib/products';
import { cx } from '../lib/format';
import type { ListFilters, User } from '../lib/types';
import { CloseIcon, FilterIcon, SearchIcon } from './primitives';

/** Toggles a value in/out of an array filter. */
function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export function FilterBar({
  filters,
  onChange,
  people,
  isAdmin,
  resultCount,
}: {
  filters: ListFilters;
  onChange: (patch: Partial<ListFilters>) => void;
  people: User[];
  isAdmin: boolean;
  resultCount: number | null;
}) {
  const [expanded, setExpanded] = useState(false);
  // Local mirror so typing feels instant; the parent debounces the request.
  const [searchDraft, setSearchDraft] = useState(filters.q);
  const searchRef = useRef<HTMLInputElement>(null);

  // Keep the draft in sync when filters are reset from outside.
  useEffect(() => {
    setSearchDraft(filters.q);
  }, [filters.q]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (searchDraft !== filters.q) onChange({ q: searchDraft, page: 1 });
    }, 300);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  // "/" focuses search, as in most dashboards.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const activeCount =
    filters.product.length +
    filters.category.length +
    filters.status.length +
    (filters.visibility ? 1 : 0) +
    (filters.assignedToId ? 1 : 0) +
    (filters.minPriority > 0 ? 1 : 0) +
    (filters.scope !== 'all' ? 1 : 0);

  const reset = () =>
    onChange({
      product: [],
      category: [],
      status: [],
      visibility: '',
      assignedToId: '',
      minPriority: 0,
      scope: 'all',
      q: '',
      page: 1,
    });

  return (
    <div className="surface sticky top-4 z-30 p-4 sm:p-5">
      {/* Row 1 — search, scope, sort */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-mist-500" />
          <input
            ref={searchRef}
            type="search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search titles, descriptions and people…"
            aria-label="Search suggestions"
            className="field pl-10"
          />
          {!searchDraft && (
            <kbd className="pointer-events-none absolute top-1/2 right-3 hidden -translate-y-1/2 rounded border chip-idle px-1.5 py-0.5 text-[0.625rem] font-medium text-mist-600 sm:block">
              /
            </kbd>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl border hairline bg-ink-900/60 p-0.5">
            {(
              [
                ['all', 'All'],
                ['assigned_to_me', 'My Assigned'],
                ['submitted_by_me', 'My Reports'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => onChange({ scope: value, page: 1 })}
                aria-pressed={filters.scope === value}
                className={cx(
                  'rounded-[0.625rem] px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors duration-150',
                  filters.scope === value
                    ? 'bg-[var(--tint-bg-strong)] text-mist-50'
                    : 'text-mist-500 hover:text-mist-200',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <select
            value={filters.sort}
            onChange={(e) => onChange({ sort: e.target.value as ListFilters['sort'], page: 1 })}
            aria-label="Sort suggestions"
            className="field w-auto py-2 pr-8 text-xs font-semibold"
          >
            <option value="newest">Newest first</option>
            <option value="priority">Highest impact</option>
            <option value="status">By status</option>
            <option value="oldest">Oldest first</option>
          </select>

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className={cx(
              'btn-ghost relative',
              expanded && 'border-[var(--hairline-strong)] bg-[var(--tint-bg-strong)]',
            )}
          >
            <FilterIcon className="h-4 w-4" />
            Filters
            {activeCount > 0 && (
              <span className="ml-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-lotus-500 px-1.5 text-[0.625rem] font-bold text-white">
                {activeCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Row 2 — the detailed filter panel */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-4 grid gap-5 hairline border-t pt-4 md:grid-cols-2 xl:grid-cols-4">
              <FilterGroup label="Product">
                {PRODUCTS.map((p) => {
                  const on = filters.product.includes(p);
                  const t = PRODUCT_THEME[p];
                  return (
                    <TogglePill
                      key={p}
                      active={on}
                      onClick={() => onChange({ product: toggle(filters.product, p), page: 1 })}
                      activeClass={t.chip}
                    >
                      <span className={cx('h-1.5 w-1.5 rounded-full', t.dot)} />
                      {t.label}
                    </TogglePill>
                  );
                })}
              </FilterGroup>

              <FilterGroup label="Category">
                {CATEGORIES.map((c) => (
                  <TogglePill
                    key={c}
                    active={filters.category.includes(c)}
                    onClick={() => onChange({ category: toggle(filters.category, c), page: 1 })}
                    activeClass="border-orchid-400/40 bg-orchid-400/12 text-orchid-300"
                  >
                    {CATEGORY_META[c].label}
                  </TogglePill>
                ))}
              </FilterGroup>

              <FilterGroup label="Status">
                {STATUSES.map((s) => (
                  <TogglePill
                    key={s}
                    active={filters.status.includes(s)}
                    onClick={() => onChange({ status: toggle(filters.status, s), page: 1 })}
                    activeClass={STATUS_META[s].chip}
                  >
                    <span className={cx('h-1.5 w-1.5 rounded-full', STATUS_META[s].dot)} />
                    {STATUS_META[s].label}
                  </TogglePill>
                ))}
              </FilterGroup>

              <FilterGroup label="Minimum impact">
                {[0, 3, 4, 5].map((n) => (
                  <TogglePill
                    key={n}
                    active={filters.minPriority === n}
                    onClick={() => onChange({ minPriority: n, page: 1 })}
                    activeClass="border-lotus-400/40 bg-lotus-400/12 text-lotus-200"
                  >
                    {n === 0 ? 'Any' : `${n}+`}
                  </TogglePill>
                ))}
              </FilterGroup>

              <FilterGroup label="Visibility">
                {(
                  [
                    ['', 'Any'],
                    ['public', 'Public'],
                    ['private', 'Private'],
                  ] as const
                ).map(([value, label]) => (
                  <TogglePill
                    key={value || 'any'}
                    active={filters.visibility === value}
                    onClick={() => onChange({ visibility: value, page: 1 })}
                    activeClass="border-amber-400/40 bg-amber-400/12 text-amber-300"
                  >
                    {label}
                  </TogglePill>
                ))}
              </FilterGroup>

              {isAdmin && people.length > 0 && (
                <FilterGroup label="Assignee" className="md:col-span-2">
                  <select
                    value={filters.assignedToId}
                    onChange={(e) => onChange({ assignedToId: e.target.value, page: 1 })}
                    aria-label="Filter by assignee"
                    className="field text-xs"
                  >
                    <option value="">Anyone</option>
                    {people.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.title ? ` — ${p.title}` : ''}
                      </option>
                    ))}
                  </select>
                </FilterGroup>
              )}
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 hairline border-t pt-3">
              <p className="text-xs text-mist-500">
                {resultCount === null
                  ? 'Loading…'
                  : `${resultCount} suggestion${resultCount === 1 ? '' : 's'} match`}
              </p>
              {activeCount > 0 && (
                <button type="button" onClick={reset} className="btn-subtle text-xs">
                  <CloseIcon className="h-3.5 w-3.5" />
                  Clear all filters
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FilterGroup({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <fieldset className={className}>
      <legend className="label">{label}</legend>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </fieldset>
  );
}

function TogglePill({
  active,
  onClick,
  activeClass,
  children,
}: {
  active: boolean;
  onClick: () => void;
  activeClass: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'chip cursor-pointer transition-all duration-150',
        active ? activeClass : 'chip-idle text-mist-500 hover:text-mist-200',
      )}
    >
      {children}
    </button>
  );
}
